/**
 * 共有プリミティブコンポーネント
 * すべて CSS 変数（var(--ls-*)）のみを使用、hardcoded 色は禁止。
 */
import type { CSSProperties, PropsWithChildren, ReactNode } from 'react';

export interface SectionFrameProps {
  className?: string;
  style?: CSSProperties;
  /** 背景色を変えたいときに使用（デフォルトは identity の bg） */
  background?: 'bg' | 'surface' | 'surface-alt';
}

export function SectionFrame({
  children,
  className,
  style,
  background = 'bg',
}: PropsWithChildren<SectionFrameProps>) {
  const bgVar =
    background === 'surface'
      ? 'var(--ls-surface)'
      : background === 'surface-alt'
        ? 'var(--ls-surface-alt)'
        : 'var(--ls-bg)';
  return (
    <section
      className={`ls-section ${className ?? ''}`.trim()}
      style={{ background: bgVar, ...style }}
    >
      <div className="ls-section-inner">{children}</div>
    </section>
  );
}

export function Eyebrow({ children }: PropsWithChildren) {
  if (!children) return null;
  return <p className="ls-eyebrow">{children}</p>;
}

interface TypographyProps {
  className?: string;
  style?: CSSProperties;
}

export function H1({ children, className, style }: PropsWithChildren<TypographyProps>) {
  return <h1 className={`ls-h1 ${className ?? ''}`.trim()} style={style}>{children}</h1>;
}

export function H2({ children, className, style }: PropsWithChildren<TypographyProps>) {
  return <h2 className={`ls-h2 ${className ?? ''}`.trim()} style={style}>{children}</h2>;
}

export function H3({ children, className, style }: PropsWithChildren<TypographyProps>) {
  return <h3 className={`ls-h3 ${className ?? ''}`.trim()} style={style}>{children}</h3>;
}

export function Body({ children, className, style }: PropsWithChildren<TypographyProps>) {
  return <p className={`ls-body ${className ?? ''}`.trim()} style={style}>{children}</p>;
}

export interface BtnProps {
  variant?: 'primary' | 'secondary' | 'ghost';
  href?: string;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
}

export function Btn({
  variant = 'primary',
  href,
  onClick,
  children,
  className,
}: BtnProps) {
  const cls = `ls-btn ls-btn-${variant} ${className ?? ''}`.trim();
  if (href) {
    return (
      <a href={href} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

export function Badge({ children }: PropsWithChildren) {
  if (!children) return null;
  return <span className="ls-badge">{children}</span>;
}

export type ImageFit = 'cover' | 'contain' | 'natural';

export interface VisualPlaceholderProps {
  src?: string | null;
  alt?: string;
  aspectRatio?: string;
  label?: string;
  className?: string;
  /**
   * 画像表示方法:
   *   cover   (default) — 枠に合わせて切り取り、アスペクト比固定
   *   contain          — 画像全体を表示（余白あり）、アスペクト比固定
   *   natural          — 画像本来の比率を維持し、枠が伸縮
   */
  fit?: ImageFit;
}

/**
 * 画像枠。src があれば <img>、なければ deterministic gradient のプレースホルダ。
 * fit prop で表示方法を指定可能。
 */
export function VisualPlaceholder({
  src,
  alt,
  aspectRatio = '16 / 10',
  label,
  className,
  fit = 'cover',
}: VisualPlaceholderProps) {
  if (src) {
    if (fit === 'natural') {
      // アスペクト比を画像に合わせ、枠を伸縮（aspect-ratio なし）
      return (
        <div
          className={`ls-visual ${className ?? ''}`.trim()}
          style={{ aspectRatio: 'auto', height: 'auto' }}
        >
          <img
            src={src}
            alt={alt ?? ''}
            loading="lazy"
            style={{ width: '100%', height: 'auto', objectFit: 'initial' }}
          />
        </div>
      );
    }
    if (fit === 'contain') {
      return (
        <div
          className={`ls-visual ${className ?? ''}`.trim()}
          style={{ aspectRatio }}
        >
          <img
            src={src}
            alt={alt ?? ''}
            loading="lazy"
            style={{ objectFit: 'contain' }}
          />
        </div>
      );
    }
    // cover (default)
    return (
      <div
        className={`ls-visual ${className ?? ''}`.trim()}
        style={{ aspectRatio }}
      >
        <img src={src} alt={alt ?? ''} loading="lazy" />
      </div>
    );
  }
  // ラベルから決定論的な hue を生成（gradient プレースホルダ）
  const hue = label
    ? [...label].reduce((a, c) => a + c.charCodeAt(0), 0) % 360
    : 220;
  return (
    <div
      className={`ls-visual ${className ?? ''}`.trim()}
      style={{
        aspectRatio,
        background: `linear-gradient(135deg, hsl(${hue} 60% 70%), hsl(${(hue + 60) % 360} 60% 60%))`,
      }}
      aria-label={label}
    >
      {label && (
        <span
          style={{
            color: 'rgba(255,255,255,0.85)',
            fontSize: '0.85rem',
            fontWeight: 600,
            padding: '6px 12px',
            background: 'rgba(0,0,0,0.2)',
            borderRadius: 999,
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}

export interface AvatarProps {
  src?: string | null;
  alt?: string;
  name?: string;
  size?: number;
}

export function Avatar({ src, alt, name, size = 40 }: AvatarProps) {
  const initials = (name ?? '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
  const hue = name
    ? [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360
    : 220;
  if (src) {
    return (
      <img
        src={src}
        alt={alt ?? name ?? ''}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
        }}
        loading="lazy"
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `hsl(${hue} 50% 60%)`,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.4,
        fontWeight: 600,
        flexShrink: 0,
      }}
      aria-label={name}
    >
      {initials}
    </div>
  );
}

export function LogoMark({ name }: { name: string }) {
  return (
    <span
      style={{
        fontWeight: 600,
        fontSize: '1.1rem',
        letterSpacing: '0.05em',
        color: 'var(--ls-text)',
      }}
    >
      {name}
    </span>
  );
}

/** SVG icon helpers */
export function CheckIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function ArrowRightIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

export function PlayIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <polygon points="6 4 20 12 6 20 6 4" />
    </svg>
  );
}

export function PlusIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
